import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Link } from "react-router-dom";
import { Sparkles, Info, BookOpen, FileText } from "lucide-react";

export default function DemoPage() {
  const { lang } = useLanguage();
  const [demo, setDemo] = useState(null);
  const [err, setErr] = useState(null);
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    api
      .get("/public/demo-preview")
      .then((r) => {
        setDemo(r.data.demo);
        setRemaining(r.data.rate_limit.remaining);
      })
      .catch((e) => setErr(formatApiError(e)));
  }, []);

  const isAr = lang === "ar";

  return (
    <main className="min-h-[calc(100vh-4rem)] px-6 py-12" data-testid="demo-page">
      <div className="mx-auto max-w-4xl">
        <h1 className="font-heading text-4xl font-bold text-slate-900 mb-2 text-start">
          {isAr ? "عيّنة هيكلية" : "Structural sample"}
        </h1>

        {/* Friendly intro line (beginner-friendly) */}
        {demo?.intro_line && (
          <div
            className="flex items-start gap-2 p-4 mb-6 rounded-lg border border-emerald-200 bg-emerald-50/60"
            data-testid="demo-intro-line"
          >
            <Info className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
            <p className="text-sm text-emerald-900 leading-relaxed text-start">
              {isAr ? demo.intro_line : "Here's what your paper will look like — an illustrative sample for every section. The paid version contains full content and real verified references."}
            </p>
          </div>
        )}

        {err && (
          <Alert variant="destructive" data-testid="demo-error-alert">
            <AlertDescription>{err}</AlertDescription>
          </Alert>
        )}

        {demo && (
          <>
            <Card className="p-8 border border-slate-200 shadow-none mb-6">
              <p className="text-xs text-slate-500 mb-1 text-start">
                {isAr ? "العنوان (مثال)" : "Title (example)"}
              </p>
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-7 text-start" data-testid="demo-title">
                {demo.title}
              </h2>

              <div className="space-y-7">
                {/* Abstract — realistic sample */}
                <div>
                  <h3 className="font-heading text-sm font-bold text-slate-700 mb-2 text-start">
                    {isAr ? "الملخّص (نموذج)" : "Abstract (example)"}
                  </h3>
                  <p
                    className="text-sm text-slate-700 leading-loose text-start"
                    data-testid="demo-abstract"
                  >
                    {demo.abstract_skeleton}
                  </p>
                  {demo.abstract_example_note && (
                    <p className="text-[11px] text-slate-400 mt-1.5 text-start italic">
                      {demo.abstract_example_note}
                    </p>
                  )}
                  {Array.isArray(demo.keywords) && demo.keywords.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3" data-testid="demo-keywords">
                      <span className="text-[11px] text-slate-500">
                        {isAr ? "الكلمات المفتاحية:" : "Keywords:"}
                      </span>
                      {demo.keywords.map((k, i) => (
                        <span
                          key={i}
                          className="text-[11px] px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-md"
                        >
                          {k}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Section structure with plain-language descriptions */}
                <div>
                  <h3 className="font-heading text-sm font-bold text-slate-700 mb-3 text-start">
                    {isAr ? "هيكل المباحث" : "Sections"}
                  </h3>
                  <ol className="space-y-3 text-sm list-decimal ps-6 text-start" data-testid="demo-sections">
                    {demo.sections.map((s, i) => (
                      <li key={i} className="text-slate-800">
                        <b className="text-slate-900">{s.title}</b>
                        {s.description && (
                          <p
                            className="text-xs text-slate-600 mt-1 leading-relaxed"
                            data-testid={`demo-section-description-${i}`}
                          >
                            {s.description}
                          </p>
                        )}
                        {Array.isArray(s.subsections) && s.subsections.length > 0 && (
                          <p className="text-[11px] text-slate-400 mt-1">
                            {s.subsections.join(" · ")}
                          </p>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Critical comparison table — populated, beginner-friendly */}
                <div>
                  <h3 className="font-heading text-sm font-bold text-slate-700 mb-2 text-start">
                    {isAr ? "نموذج جدول نقدي مقارن" : "Sample critical comparison table"}
                  </h3>
                  <div className="overflow-x-auto rounded-md border border-slate-300">
                    <table className="text-xs w-full" data-testid="demo-table">
                      <tbody>
                        {demo.table_skeleton_rows.map((row, i) => {
                          const isHeader = i === 0;
                          const isHighlight = i === demo.table_highlight_row_index;
                          return (
                            <tr
                              key={i}
                              className={
                                isHeader
                                  ? "bg-slate-900 text-white font-bold"
                                  : isHighlight
                                  ? "bg-amber-50 font-semibold"
                                  : ""
                              }
                              data-testid={isHighlight ? "demo-table-current-study-row" : undefined}
                            >
                              {row.map((cell, j) => (
                                <td
                                  key={j}
                                  className={`border border-slate-300 px-3 py-2 text-start align-top ${
                                    isHighlight ? "border-amber-300" : ""
                                  }`}
                                >
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1.5 text-start italic">
                    {isAr
                      ? "الصف المظلَّل يمثّل موقع دراستك ضمن التيارات النظرية."
                      : "The highlighted row marks where your study sits among the literature."}
                  </p>
                </div>

                {/* Equation with legend */}
                <div>
                  <h3 className="font-heading text-sm font-bold text-slate-700 mb-2 text-start">
                    {isAr ? "نموذج معادلة (مع تعريف الرموز)" : "Equation example (with symbol legend)"}
                  </h3>
                  <div
                    className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-md text-center font-serif text-lg text-slate-900"
                    data-testid="demo-equation"
                  >
                    {demo.equation_skeleton}
                  </div>
                  {demo.equation_legend && (
                    <p
                      className="text-xs text-slate-600 mt-2 leading-relaxed text-start"
                      data-testid="demo-equation-legend"
                    >
                      {demo.equation_legend}
                    </p>
                  )}
                </div>

                {/* APA reference examples */}
                {Array.isArray(demo.references_examples) && demo.references_examples.length > 0 && (
                  <div>
                    <h3 className="font-heading text-sm font-bold text-slate-700 mb-2 text-start flex items-center gap-1.5">
                      <BookOpen className="w-4 h-4" />
                      {isAr ? "نماذج من قائمة المراجع (APA)" : "Reference examples (APA)"}
                    </h3>
                    <ul className="space-y-2 text-sm" data-testid="demo-references">
                      {demo.references_examples.map((ref, i) => (
                        <li
                          key={i}
                          className="text-slate-700 leading-relaxed text-start ps-3 border-s-2 border-slate-200"
                          dir="ltr"
                          style={{ textAlign: "start" }}
                        >
                          {ref}
                        </li>
                      ))}
                    </ul>
                    {demo.references_examples_note && (
                      <p className="text-[11px] text-slate-400 mt-2 text-start italic">
                        {demo.references_examples_note}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div
                className="flex items-start gap-2 mt-8 p-3 rounded-md bg-slate-50 border border-slate-200"
                data-testid="demo-footer-note"
              >
                <FileText className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
                <p className="text-xs text-slate-600 leading-relaxed text-start">
                  {demo.footer_note}
                </p>
              </div>
            </Card>

            <div className="flex flex-wrap items-center justify-between gap-3 px-1">
              <p className="text-[11px] text-slate-500">
                {isAr ? "متبقّي اليوم: " : "Remaining today: "}
                <b>{remaining}</b>
              </p>
              <Link to="/register">
                <Button
                  className="bg-slate-900 hover:bg-slate-800 gap-1.5"
                  data-testid="demo-cta-register"
                >
                  <Sparkles className="w-4 h-4" />
                  {isAr ? "اطلب بحثاً حقيقياً الآن" : "Order a real paper now"}
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
